
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useState } from "react"
import { useCondominiums } from "@/hooks/useCondominiums"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"

interface CreateUserModalProps {
  isOpen: boolean
  onClose: () => void
}

const CreateUserModal = ({ isOpen, onClose }: CreateUserModalProps) => {
  const { data: condominiums } = useCondominiums()
  const queryClient = useQueryClient()
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    full_name: "",
    role: "morador" as string,
    condominium_id: ""
  })
  const [isCreating, setIsCreating] = useState(false)
  
  const selectedCondominium = condominiums?.find(c => c.id === newUser.condominium_id)
  const isMarket = selectedCondominium?.type === "ponto_eletronico"
  const isAcessos = selectedCondominium?.type === "controle_acessos"

  const getTypeLabel = (type: string | null | undefined) => {
    if (type === "ponto_eletronico") return "Ponto Eletrônico"
    if (type === "controle_acessos") return "Controle de Acessos"
    return "Condomínio"
  }

  const handleCreate = async () => {
    if (!newUser.email || !newUser.password || !newUser.full_name) {
      toast.error("Preencha todos os campos obrigatórios")
      return
    }

    if (!newUser.condominium_id) {
      toast.error("Selecione um condomínio")
      return
    }

    setIsCreating(true)

    try {
      console.log('Criando usuário com dados:', {
        email: newUser.email,
        role: newUser.role,
        full_name: newUser.full_name,
        condominium_id: newUser.condominium_id
      })

      // Usar signup normal em vez de admin.createUser
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: newUser.email,
        password: newUser.password,
        options: {
          data: {
            full_name: newUser.full_name,
            role: newUser.role,
            app_code: 'itacondo'
          }
        }
      })

      if (authError) {
        console.error("Erro de autenticação:", authError)
        throw authError
      }

      console.log('Usuário criado na auth:', authData.user?.id)

      // Se o usuário foi criado, atualizar o perfil com o condomínio
      if (authData.user) {
        console.log('Atualizando perfil com condomínio...')
        
        // Aguardar um pouco para garantir que o trigger executou
        await new Promise(resolve => setTimeout(resolve, 2000))
        
        const { error: profileError } = await supabase
          .from("profiles")
          .update({
            condominium_id: newUser.condominium_id,
            full_name: newUser.full_name,
            role: newUser.role as any
          })
          .eq("id", authData.user.id)

        if (profileError) {
          console.error("Erro ao atualizar perfil:", profileError)
          toast.error("Usuário criado mas houve erro ao configurar perfil: " + profileError.message)
        } else {
          console.log('Perfil atualizado com sucesso')
        }
      }

      toast.success("Usuário criado com sucesso!")
      
      // Refresh das queries
      queryClient.invalidateQueries({ queryKey: ["profiles"] })
      
      // Reset form
      setNewUser({ 
        email: "", 
        password: "", 
        full_name: "", 
        role: "morador", 
        condominium_id: "" 
      })
      onClose()
    } catch (error: any) {
      console.error("Erro ao criar usuário:", error)
      let errorMessage = "Erro ao criar usuário"
      
      if (error.message?.includes("User already registered")) {
        errorMessage = "Este email já está cadastrado"
      } else if (error.message?.includes("Password")) {
        errorMessage = "A senha deve ter pelo menos 6 caracteres"
      } else if (error.message?.includes("Signup is disabled")) {
        errorMessage = "Cadastro está desabilitado. Entre em contato com o administrador."
      } else if (error.message) {
        errorMessage = error.message
      }
      
      toast.error(errorMessage)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Criar Novo Usuário</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="user-email">Email *</Label>
            <Input
              id="user-email"
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({...newUser, email: e.target.value})}
              placeholder="email@exemplo.com"
              disabled={isCreating}
            />
          </div>
          <div>
            <Label htmlFor="user-password">Senha *</Label>
            <Input
              id="user-password"
              type="password"
              value={newUser.password}
              onChange={(e) => setNewUser({...newUser, password: e.target.value})}
              placeholder="Mínimo 6 caracteres"
              disabled={isCreating}
            />
          </div>
          <div>
            <Label htmlFor="user-name">Nome Completo *</Label>
            <Input
              id="user-name"
              value={newUser.full_name}
              onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
              placeholder="Nome completo"
              disabled={isCreating}
            />
          </div>
          <div>
            <Label htmlFor="user-condominium">Local *</Label>
            <Select 
              value={newUser.condominium_id} 
              onValueChange={(value) => {
                const selected = condominiums?.find(c => c.id === value)
                const defaultRole = (selected?.type === "ponto_eletronico" || selected?.type === "controle_acessos") ? "porteiro" : "morador"
                setNewUser({
                  ...newUser, 
                  condominium_id: value,
                  role: defaultRole
                })
              }}
              disabled={isCreating}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um local" />
              </SelectTrigger>
              <SelectContent>
                {condominiums?.map((condominium) => (
                  <SelectItem key={condominium.id} value={condominium.id}>
                    {condominium.name} ({getTypeLabel(condominium.type)})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="user-role">Perfil *</Label>
            <Select 
              value={newUser.role} 
              onValueChange={(value: any) => setNewUser({...newUser, role: value})}
              disabled={isCreating || !newUser.condominium_id}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {isMarket ? (
                  <>
                    <SelectItem value="porteiro">Funcionário</SelectItem>
                    <SelectItem value="administracao">Administração</SelectItem>
                  </>
                ) : isAcessos ? (
                  <>
                    <SelectItem value="porteiro">Porteiro</SelectItem>
                    <SelectItem value="administracao">Administração</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="morador">Morador</SelectItem>
                    <SelectItem value="porteiro">Porteiro</SelectItem>
                    <SelectItem value="sindico">Síndico</SelectItem>
                    <SelectItem value="administracao">Administração</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleCreate} className="w-full" disabled={isCreating}>
            {isCreating ? "Criando..." : "Criar Usuário"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export { CreateUserModal }
